import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Popper,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { KeyboardArrowDown, OpenInNew } from '@material-ui/icons';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';

import { SettingsContext } from '../../contexts/SettingsContext';
import useFrequentlyUsedModules from '../../hooks/useFrequentlyUsedModules';

/** Bars scale to page count; floor avoids the top row always showing 100% when counts are small (e.g. 3). */
const PAGE_COUNT_BAR_VISUAL_FLOOR = 10;
const MODULE_LIST_MAX_HEIGHT = 320;
const MODULE_FETCH_LIMIT = 50;
const POPOVER_CLOSE_DELAY_MS = 350;
/** Full "Frequently Used" label above this width; at 1366px and below, icon only. */
const FREQUENTLY_USED_FULL_LABEL_MIN_WIDTH = 1367;

export default function FrequentlyUsedPopover() {
  const theme = useTheme();
  const showFullNavLabel = useMediaQuery(`(min-width:${FREQUENTLY_USED_FULL_LABEL_MIN_WIDTH}px)`);
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const closeTimerRef = useRef(null);
  const loggedUserData = JSON.parse(window.localStorage.getItem('userData') || 'null');
  const { setColor, themeMode } = useContext(SettingsContext);

  const [open, setOpen] = useState(false);
  const [fetchEnabled, setFetchEnabled] = useState(false);

  const { modules, loading, error } = useFrequentlyUsedModules({
    userId: loggedUserData?.id,
    enabled: fetchEnabled,
    limit: MODULE_FETCH_LIMIT,
  });

  const maxPageCount = useMemo(
    () => Math.max(...modules.map((module) => module.pageCount), 1),
    [modules]
  );

  const pageCountBarScale = useMemo(
    () => Math.max(maxPageCount, PAGE_COUNT_BAR_VISUAL_FLOOR),
    [maxPageCount]
  );

  const getThemeColor = () => (themeMode === 'light' ? setColor?.main : setColor?.dark);

  const themeColor = getThemeColor() || theme.palette.primary.main;

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handleOpen = () => {
    clearCloseTimer();
    if (!fetchEnabled) {
      setFetchEnabled(true);
    }
    setOpen(true);
  };

  const handleClose = () => {
    clearCloseTimer();
    setOpen(false);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
    }, POPOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => () => clearCloseTimer(), []);

  const handleModuleClick = (module) => {
    if (!module?.path) {
      return;
    }
    handleClose();
    navigate(module.path);
  };

  const getAccentSurface = (accentKey) => {
    const paletteKey = theme.palette[accentKey];
    const main = paletteKey?.main || theme.palette.primary.main;
    return {
      backgroundColor: alpha(main, 0.12),
      border: `1px solid ${alpha(main, 0.2)}`,
    };
  };

  return (
    <>
      <Button
        ref={anchorRef}
        disableRipple
        onMouseEnter={handleOpen}
        onMouseLeave={scheduleClose}
        onClick={handleOpen}
        endIcon={
          showFullNavLabel ? (
            <KeyboardArrowDown
              fontSize="small"
              sx={{ color: (t) => t.palette.functionalityBtn }}
            />
          ) : undefined
        }
        sx={{
          mx: { xs: 0.25, lg: 1 },
          px: { xs: 0.75, lg: 1.25 },
          py: 0.5,
          borderRadius: 1.5,
          minWidth: 'auto',
          flexShrink: 0,
          border: `1px solid ${alpha(themeColor, 0.35)}`,
          backgroundColor: alpha(themeColor, 0.08),
          '&:hover': { backgroundColor: alpha(themeColor, 0.14) },
        }}
      >
        <BoltOutlinedIcon
          sx={{
            fontSize: { xs: 18, lg: 20 },
            color: themeColor,
          }}
        />
        {showFullNavLabel && (
          <Typography
            sx={{
              color: (t) => t.palette.functionalityBtn,
              fontSize: { lg: '12px', newLg: '13px', xl: '14px' },
              fontWeight: 'bold',
              pl: 0.75,
              whiteSpace: 'nowrap',
            }}
          >
            Frequently Used
          </Typography>
        )}
      </Button>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        disablePortal={false}
        style={{ zIndex: theme.zIndex.modal }}
        modifiers={{
          flip: { enabled: true },
          preventOverflow: { enabled: true, boundariesElement: 'viewport' },
          offset: { enabled: true, offset: '0, 6' },
        }}
      >
        <Paper
          elevation={8}
          onMouseEnter={handleOpen}
          onMouseLeave={scheduleClose}
          sx={{
            width: { xs: 'min(94vw, 420px)', sm: 420 },
            maxHeight: 'min(85vh, 480px)',
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.background.paper,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -10,
              left: 0,
              right: 0,
              height: 10,
            },
          }}
        >
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: themeColor,
                backgroundColor: alpha(themeColor, 0.12),
              }}
            >
              <BoltOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.25,
                  color: theme.palette.text.primary,
                }}
              >
                Frequently Used
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                Quick access to your most used modules
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: alpha(theme.palette.text.primary, 0.02),
          }}
        >
          <Typography
            variant="caption"
            sx={{
              flex: 1,
              fontWeight: 700,
              letterSpacing: 0.6,
              color: 'text.secondary',
              textTransform: 'uppercase',
              fontSize: '0.65rem',
            }}
          >
            Module
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.4,
              color: 'text.secondary',
              textTransform: 'uppercase',
              fontSize: '0.65rem',
              whiteSpace: 'nowrap',
            }}
          >
            Page Count
          </Typography>
        </Box>

        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            pb: 1.5,
            flex: 1,
            minHeight: 0,
            maxHeight: MODULE_LIST_MAX_HEIGHT,
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: 6,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.text.primary, 0.2),
              borderRadius: 8,
            },
          }}
        >
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loading && error && (
            <Typography variant="body2" color="error" sx={{ px: 0.5, py: 2 }}>
              Unable to load frequently used modules.
            </Typography>
          )}

          {!loading && !error && modules.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 0.5, py: 2 }}>
              No frequently used modules yet.
            </Typography>
          )}

          {!loading &&
            !error &&
            modules.map((module) => {
              const progressValue = Math.min(
                100,
                Math.round((module.pageCount / pageCountBarScale) * 100)
              );
              const isClickable = Boolean(module.path);
              const accentSurface = getAccentSurface(module.accent);

              const rowHoverBg = themeColor || theme.palette.primary.main;

              return (
                <Box
                  key={module.id}
                  role="button"
                  tabIndex={isClickable ? 0 : -1}
                  onClick={() => handleModuleClick(module)}
                  onKeyDown={(event) => {
                    if (!isClickable) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleModuleClick(module);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1,
                    py: 1.25,
                    borderRadius: 1.5,
                    cursor: isClickable ? 'pointer' : 'default',
                    transition: theme.transitions.create(['background-color'], {
                      duration: theme.transitions.duration.shorter,
                    }),
                    ...(isClickable && {
                      '&:hover, &:focus-visible': {
                        backgroundColor: rowHoverBg,
                        '& .frequently-used-module-title': {
                          color: theme.palette.common.white,
                        },
                        '& .frequently-used-module-description': {
                          color: theme.palette.success.light,
                        },
                        '& .frequently-used-module-stats': {
                          opacity: 0,
                          visibility: 'hidden',
                        },
                        '& .frequently-used-module-open': {
                          opacity: 1,
                          visibility: 'visible',
                        },
                        '& .frequently-used-module-icon-wrap': {
                          backgroundColor: alpha(theme.palette.common.white, 0.12),
                          borderColor: alpha(theme.palette.common.white, 0.28),
                        },
                        '& .frequently-used-module-icon': {
                          filter: 'brightness(0) invert(1)',
                        },
                      },
                    }),
                  }}
                >
                  <Box
                    className="frequently-used-module-icon-wrap"
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: theme.transitions.create(['background-color', 'border-color'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                      ...accentSurface,
                    }}
                  >
                    <Box
                      className="frequently-used-module-icon"
                      component="img"
                      alt=""
                      src={module.iconSrc}
                      sx={{
                        width: 24,
                        height: 24,
                        objectFit: 'contain',
                        transition: theme.transitions.create('filter', {
                          duration: theme.transitions.duration.shorter,
                        }),
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      className="frequently-used-module-title"
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.25,
                        color: themeColor,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        transition: theme.transitions.create('color', {
                          duration: theme.transitions.duration.shorter,
                        }),
                      }}
                    >
                      {module.label}
                    </Typography>
                    <Typography
                      className="frequently-used-module-description"
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        transition: theme.transitions.create('color', {
                          duration: theme.transitions.duration.shorter,
                        }),
                      }}
                    >
                      {module.description}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      position: 'relative',
                      flexShrink: 0,
                      minWidth: 108,
                      minHeight: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Stack
                      className="frequently-used-module-stats"
                      alignItems="flex-end"
                      spacing={0.5}
                      sx={{
                        transition: theme.transitions.create(['opacity', 'visibility'], {
                          duration: theme.transitions.duration.shorter,
                        }),
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1 }}>
                        {module.pageCount}
                      </Typography>
                      <Box
                        sx={{
                          width: 96,
                          height: 8,
                          borderRadius: 4,
                          overflow: 'hidden',
                          backgroundColor: alpha(theme.palette.text.primary, 0.08),
                        }}
                      >
                        <Box
                          sx={{
                            width: `${progressValue}%`,
                            height: '100%',
                            borderRadius: 4,
                            backgroundColor: themeColor,
                          }}
                        />
                      </Box>
                    </Stack>

                    <Stack
                      className="frequently-used-module-open"
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        opacity: 0,
                        visibility: 'hidden',
                        color: theme.palette.common.white,
                        transition: theme.transitions.create(['opacity', 'visibility'], {
                          duration: theme.transitions.duration.shorter,
                        }),
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1 }}>
                        Open
                      </Typography>
                      <OpenInNew sx={{ fontSize: 16 }} />
                    </Stack>
                  </Box>
                </Box>
              );
            })}
        </Box>
        </Paper>
      </Popper>
    </>
  );
}
